// VerificarCodigo.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Heading,
    FormControl,
    FormLabel,
    Input,
    Text,
    VStack,
    useToast,
    Spinner
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const VerificarCodigo = () => {
    const { t } = useTranslation();
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();

    const [codigo, setCodigo] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [timeoutActive, setTimeoutActive] = useState(false);
    const [remainingTime, setRemainingTime] = useState(30);
    const [verificando, setVerificando] = useState(false); // Evitar llamadas duplicadas

    const searchParams = new URLSearchParams(location.search);
    const email = searchParams.get('email');
    const userTypeFromUrl = searchParams.get('type') || searchParams.get('userType') || 'cliente';

    useEffect(() => {
        if (error && !timeoutActive) {
            setTimeoutActive(true);
            setRemainingTime(30);
            const intervalId = setInterval(() => {
                setRemainingTime((prevTime) => prevTime - 1);
            }, 1000);

            setTimeout(() => {
                setTimeoutActive(false);
                setRemainingTime(30);
                clearInterval(intervalId);
                setError('');
                setCodigo('');
            }, 30000);

            return () => clearInterval(intervalId);
        }
    }, [error, timeoutActive]);

    const verificarCodigo = useCallback(async () => {
        if (verificando) return; // Evitar llamadas duplicadas
        
        setVerificando(true);
        setLoading(true);
        setError('');

        try {
            const baseURL = process.env.REACT_APP_API_BASE_URL;
            console.log('Enviando verificación:', {
                email: email?.trim(),
                codigo: codigo?.trim(),
                user_type: userTypeFromUrl
            });
            
            // Generar o recuperar custom_device_id
            let customDeviceId = localStorage.getItem('custom_device_id');
            if (!customDeviceId) {
                customDeviceId = `device-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
                localStorage.setItem('custom_device_id', customDeviceId);
            }

            // Construir device_info similar al login
            const device_info = {
                user_agent: navigator.userAgent || null,
                accept_language: navigator.language || null,
                app_version: navigator.appVersion || null,
                platform: navigator.platform || null,
                vendor: navigator.vendor || null,
                origin: window.location.origin,
                referer: document.referrer || null,
                connection_type: navigator.connection ? navigator.connection.effectiveType : null,
                x_requested_with: "XMLHttpRequest",
                custom_device_id: customDeviceId
            };
            
            const response = await axios.post(`${baseURL}/api/usuarios/verificar-codigo/`, {
                email: email?.trim(),
                codigo: codigo?.trim(),
                user_type: userTypeFromUrl,
                device_info: device_info
            });

            const { access_token, session_id, user_id, user_type, email: userEmail, nombre, apellidos } = response.data;

            if (access_token && session_id && user_id && user_type && userEmail) {
                // Guardar datos de sesión usando AuthContext
                login({
                    access_token,
                    session_id,
                    user_id,
                    user_type,
                    email: userEmail,
                    nombre,
                    apellidos
                });

                // Mostrar mensaje de éxito
                toast({
                    title: t('verificar_codigo_toast_title'),
                    description: t('verificar_codigo_toast_desc'),
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });

                // Redirigir después de mostrar el mensaje
                setTimeout(() => {
                    if (user_type === 'cliente') {
                        navigate('/');
                    } else if (user_type === 'conductor') {
                        navigate('/conductor');
                    } else {
                        navigate('/');
                    }
                }, 1500);
            } else {
                throw new Error(t('verificar_codigo_error_respuesta'));
            }

        } catch (err) {
            console.error('Error en verificación:', err.response?.data || err.message);
            setVerificando(false); // Permitir reintentar
            const errorMsg = err.response?.data?.detail || err.message || t('verificar_codigo_error');
            setError(errorMsg);
            setCodigo('');
        } finally {
            setLoading(false);
        }
    }, [codigo, email, login, navigate, t, toast, userTypeFromUrl, verificando]);

    useEffect(() => {
        if (codigo.length === 6 && !loading && !error && email && userTypeFromUrl && !verificando) {
            verificarCodigo();
        }
    }, [codigo, loading, error, email, userTypeFromUrl, verificando, verificarCodigo]);

    return (
        <Box p={4} maxWidth="md" mx="auto">
            <Heading as="h2" size="lg" mb={4}>
                {t('verificar_codigo_titulo')}
            </Heading>
            <Text mb={4}>
                {t('verificar_codigo_intro', { email })}
            </Text>
            <Box as="form">
                <VStack spacing={4} align="stretch">
                    <FormControl id="codigo">
                        <FormLabel>{t('verificar_codigo_label')}</FormLabel>
                        <Input
                            type="text"
                            placeholder={t('verificar_codigo_placeholder')}
                            value={codigo}
                            onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                            required
                            maxLength={6}
                            autoFocus
                            isDisabled={loading || timeoutActive}
                        />
                    </FormControl>
                    {loading && (
                        <Spinner size="md" alignSelf="center" />
                    )}
                    {error && (
                        <Text color="red.500" mt={2}>
                            {error} {timeoutActive && t('verificar_codigo_reintentar', { segundos: remainingTime })}
                        </Text>
                    )}
                </VStack>
            </Box>
        </Box>
    );
};

export default VerificarCodigo;